import { MapContainer as LeafletMapContainer, Marker, TileLayer, ZoomControl } from "react-leaflet"
import { useState, useRef, type CSSProperties } from 'react';
import 'leaflet/dist/leaflet.css';
import '../styles/MapCustomStyles.css';
import L from 'leaflet';
import Icon from '../main/Icon';
import { createCustomMarkerIcon } from './CustomMarker';
import DynamicMapStyles from './DynamicMapStyles';

// Fix Leaflet icons for Vite
import markerIcon from 'leaflet/dist/images/marker-icon.png?url';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerShadow from 'leaflet/dist/images/marker-shadow.png?url';

function Map({ latitude, longtitude }: { latitude: number, longtitude: number})
{
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [mapKey, setMapKey] = useState(0);
    const mapRef = useRef<HTMLDivElement>(null);
    const primaryColor = sessionStorage.getItem('primaryColor') || '#3498db';

    // Delete default icon and recreate to fix Vite/Webpack conflict
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    
    L.Icon.Default.mergeOptions
    ({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow
    });

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
        // Force remount of map with new key to prevent duplicate container error
        setMapKey(prev => prev + 1);
    };

    const handleEscapeKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && isFullscreen) {
            setIsFullscreen(false);
        }
    };

    return (
        <div
            ref={mapRef}
            className={isFullscreen ? 'map-wrapper map-wrapper--fullscreen' : 'map-wrapper'}
            style={{ '--brand': primaryColor } as CSSProperties}
            onKeyDown={handleEscapeKey}
            tabIndex={0}
        >
            <DynamicMapStyles primaryColor={primaryColor} />
            {!isFullscreen && (
                <button
                    type="button"
                    className="map-expand-btn"
                    onClick={toggleFullscreen}
                    title="Vollbild anzeigen"
                >
                    <Icon
                        icon="expand"
                        size="14px"
                    />
                </button>
            )}

            <div className={isFullscreen ? 'map-fs-header' : 'map-fs-header map-fs-header--hidden'}>
                <div className="map-fs-title">
                    <Icon
                        icon="map-location-dot"
                        size="20px"
                    />
                </div>

                <button
                    type="button"
                    className="map-fs-close"
                    onClick={toggleFullscreen}
                    title="Vollbild schließen (ESC)"
                >
                    <Icon
                        icon="times"
                        size="12px"
                    />
                </button>
            </div>

            <div className={isFullscreen ? 'map-canvas map-canvas--fullscreen' : 'map-canvas'}>
                <LeafletMapContainer
                    key={mapKey}
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        borderRadius: isFullscreen ? '0' : '6px',
                        border: isFullscreen ? 'none' : '1px solid rgba(0,0,0,0.1)',
                        boxShadow: isFullscreen ? 'none' : '0.8px 0.8px 5px 0px hsl(0, 0%, 0%, 0.14)'
                    }} 
                    center = {[latitude, longtitude]} 
                    zoom = {isFullscreen ? 15 : 13} 
                    scrollWheelZoom = {isFullscreen ? true : false}
                    zoomControl = {false}
                    attributionControl = {false}
                >
                    <TileLayer
                        attribution = '&copy; CARTO &copy; OpenStreetMap'
                        url = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    />

                    <ZoomControl position = 'bottomright'/>

                    <Marker position = {[latitude, longtitude]}/>
                </LeafletMapContainer>
            </div>
        </div>
    )
}

export default Map;